import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { AttendeeFieldsForm } from './AttendeeFieldsForm';
import { GuestCountStepper } from './GuestCountStepper';
import { attendeeFieldErrors, createEmptyGuest, type FieldErrors } from './utils';
import type { AttendeeFields } from '@mr-website/schemas';

export interface GuestRow extends AttendeeFields {
  id: string;
}

interface GuestSectionProps {
  guests: GuestRow[];
  guestCount: number;
  maxGuests?: number;
  errorsByGuestId: Record<string, FieldErrors>;
  onGuestCountChange: (count: number) => void;
  onGuestChange: (id: string, next: AttendeeFields) => void;
  onGuestRemove: (id: string) => void;
  onGuestFieldBlur?: (guestId: string, field: keyof AttendeeFields, values: AttendeeFields) => void;
}

export function GuestSection({
  guests,
  guestCount,
  maxGuests,
  errorsByGuestId,
  onGuestCountChange,
  onGuestChange,
  onGuestRemove,
  onGuestFieldBlur,
}: GuestSectionProps) {
  return (
    <section className="space-y-4">
      <GuestCountStepper count={guestCount} max={maxGuests} onChange={onGuestCountChange} />

      {guests.length > 0 ? (
        <div className="space-y-4">
          {guests.map((guest, index) => (
            <div key={guest.id} className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">Guest {index + 1}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-destructive hover:text-destructive"
                  onClick={() => onGuestRemove(guest.id)}
                >
                  <X className="size-4" />
                  Remove
                </Button>
              </div>
              <AttendeeFieldsForm
                idPrefix={`guest-${guest.id}`}
                values={guest}
                errors={errorsByGuestId[guest.id]}
                onChange={(next) => onGuestChange(guest.id, next)}
                onFieldBlur={(field, values) => onGuestFieldBlur?.(guest.id, field, values)}
              />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function validateGuests(guests: GuestRow[]): Record<string, FieldErrors> {
  const errors: Record<string, FieldErrors> = {};

  for (const guest of guests) {
    const guestErrors = attendeeFieldErrors(guest);
    if (Object.keys(guestErrors).length > 0) {
      errors[guest.id] = guestErrors;
    }
  }

  return errors;
}

export function syncGuestsWithCount(guests: GuestRow[], nextCount: number): GuestRow[] {
  if (nextCount === guests.length) return guests;
  if (nextCount < guests.length) return guests.slice(0, nextCount);
  return [...guests, ...Array.from({ length: nextCount - guests.length }, createEmptyGuest)];
}

export function removeGuestById(
  guests: GuestRow[],
  id: string,
): { guests: GuestRow[]; count: number } {
  const nextGuests = guests.filter((guest) => guest.id !== id);
  return { guests: nextGuests, count: nextGuests.length };
}
